import type { KanbanAlert } from "./AlertCard"

export type KanbanViewMode = "ungrouped" | "grouped"

export interface KanbanColumnSection {
  key: string
  heading: string | null
  isPinned: boolean
  alerts: KanbanAlert[]
}

const NO_SYSTEM_KEY = "__no_system__"

export function alertSeverityRank(severity: string | undefined): number {
  const s = (severity || "info").toLowerCase()
  if (s === "critical") return 1
  if (s === "warning") return 2
  return 3
}

export function sortAlertsBySeverityThenFiredAt(alerts: KanbanAlert[]): KanbanAlert[] {
  return [...alerts].sort((a, b) => {
    const ra = alertSeverityRank(a.severity)
    const rb = alertSeverityRank(b.severity)
    if (ra !== rb) return ra - rb
    const ta = Date.parse(a.fired_at) || 0
    const tb = Date.parse(b.fired_at) || 0
    return tb - ta
  })
}

/** System id/name for UI (kanban card) and grouping: context first, else explicit `systems[]`. */
export function effectiveDisplaySystem(alert: KanbanAlert): {
  id: string | null
  name: string | null
} {
  const ctx = alert.customer_site_context
  if (ctx?.system_id) {
    return {
      id: String(ctx.system_id),
      name: (ctx.system_name || "Unknown system").trim() || "Unknown system",
    }
  }
  const raw = alert.systems
  if (Array.isArray(raw) && raw.length > 0) {
    const s = raw[0] as { id?: string; name?: string | null }
    if (s?.id) {
      return {
        id: String(s.id),
        name: (s.name || "Unknown system").trim() || "Unknown system",
      }
    }
  }
  return { id: null, name: null }
}

export function primarySystemKey(alert: KanbanAlert): { key: string; name: string } {
  const { id, name } = effectiveDisplaySystem(alert)
  if (id) return { key: id, name: name || "Unknown system" }
  return { key: NO_SYSTEM_KEY, name: "No system" }
}

function minSeverityRank(alerts: KanbanAlert[]): number {
  let m = 3
  for (const a of alerts) {
    const r = alertSeverityRank(a.severity)
    if (r < m) m = r
  }
  return m
}

function maxFiredAtMs(alerts: KanbanAlert[]): number {
  let m = 0
  for (const a of alerts) {
    const t = Date.parse(a.fired_at) || 0
    if (t > m) m = t
  }
  return m
}

export function buildColumnSections(
  viewMode: KanbanViewMode,
  alerts: KanbanAlert[],
  systemOrderIds: string[],
  pinnedSystemIds: string[],
): KanbanColumnSection[] {
  const sorted = sortAlertsBySeverityThenFiredAt(alerts)
  if (viewMode === "ungrouped") {
    return [
      {
        key: "all",
        heading: null,
        isPinned: false,
        alerts: sorted,
      },
    ]
  }

  const byKey = new Map<string, { name: string; alerts: KanbanAlert[] }>()
  for (const a of sorted) {
    const { key, name } = primarySystemKey(a)
    const cur = byKey.get(key)
    if (cur) cur.alerts.push(a)
    else byKey.set(key, { name, alerts: [a] })
  }

  const keysInColumn = new Set(byKey.keys())
  const pinnedSet = new Set(pinnedSystemIds)
  const used = new Set<string>()
  const primaryOrdered: string[] = []

  for (const pid of pinnedSystemIds) {
    if (keysInColumn.has(pid)) {
      primaryOrdered.push(pid)
      used.add(pid)
    }
  }
  for (const sid of systemOrderIds) {
    if (keysInColumn.has(sid) && !used.has(sid)) {
      primaryOrdered.push(sid)
      used.add(sid)
    }
  }

  const rest = [...keysInColumn].filter((k) => !used.has(k))
  rest.sort((a, b) => {
    if (a === NO_SYSTEM_KEY && b !== NO_SYSTEM_KEY) return 1
    if (b === NO_SYSTEM_KEY && a !== NO_SYSTEM_KEY) return -1
    const la = byKey.get(a)!.alerts
    const lb = byKey.get(b)!.alerts
    const ra = minSeverityRank(la)
    const rb = minSeverityRank(lb)
    if (ra !== rb) return ra - rb
    return maxFiredAtMs(lb) - maxFiredAtMs(la)
  })

  const finalKeys = [...primaryOrdered, ...rest]

  return finalKeys.map((key) => {
    const g = byKey.get(key)!
    return {
      key,
      heading: g.name,
      isPinned: key !== NO_SYSTEM_KEY && pinnedSet.has(key),
      alerts: g.alerts,
    }
  })
}
